const loadXLSX = async () => {
  const module = await import("xlsx");
  return module.default || module;
};

const normalizeHeader = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const parseNumber = (value) => Number(String(value ?? "").replace(/,/g, "").trim());

export const parseBulkFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("Please select a file to upload"));
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const XLSX = await loadXLSX();
        const workbook = XLSX.read(new Uint8Array(event.target.result), {
          type: "array",
          cellDates: true,
        });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          resolve([]);
          return;
        }
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          raw: false,
          blankrows: true,
        });
        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Unable to read the selected file"));
    reader.readAsArrayBuffer(file);
  });

export const parseRowsForColumns = (sheetRows, columns) => {
  if (!Array.isArray(sheetRows) || sheetRows.length === 0) {
    return { rows: [], missingColumns: [] };
  }

  const headerMap = {};
  columns.forEach((column) => {
    headerMap[normalizeHeader(column.label)] = column.key;
    headerMap[normalizeHeader(column.key)] = column.key;
  });

  const headerRowIndex = sheetRows.findIndex((row) =>
    row.some((cell) => String(cell ?? "").trim() !== "")
  );

  if (headerRowIndex === -1) {
    return { rows: [], missingColumns: [] };
  }

  const headerRow = sheetRows[headerRowIndex];
  const columnIndexes = {};
  const matchedKeys = new Set();

  headerRow.forEach((cell, index) => {
    const key = headerMap[normalizeHeader(cell)];
    if (key && columnIndexes[key] === undefined) {
      columnIndexes[key] = index;
      matchedKeys.add(key);
    }
  });

  const missingColumns = columns.filter(
    (column) => column.required && !matchedKeys.has(column.key)
  );

  const rows = [];
  for (let i = headerRowIndex + 1; i < sheetRows.length; i += 1) {
    const sourceRow = sheetRows[i] || [];
    const mapped = {};
    let hasValue = false;

    columns.forEach((column) => {
      const index = columnIndexes[column.key];
      const value = index === undefined ? "" : String(sourceRow[index] ?? "").trim();
      if (value !== "") hasValue = true;
      mapped[column.key] = value;
    });

    if (hasValue) {
      rows.push({ ...mapped, __rowNumber: i + 1 });
    }
  }

  return { rows, missingColumns };
};

export const validateBulkRows = (rows, columns) =>
  rows.map((row) => {
    const errors = [];

    columns.forEach((column) => {
      const value = String(row[column.key] ?? "").trim();

      if (column.required && !value) {
        errors.push(`${column.label} is required`);
        return;
      }

      if (!value) return;

      if (
        column.type === "email" &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ) {
        errors.push(`${column.label} is not a valid email address`);
      }

      if (
        column.type === "number" &&
        (Number.isNaN(parseNumber(value)) || parseNumber(value) < 0)
      ) {
        errors.push(`${column.label} must be a valid number`);
      }
    });

    return { ...row, __errors: errors };
  });

export const buildBulkPayload = (rows, columns) =>
  rows.map((row) => {
    const payload = {};

    columns.forEach((column) => {
      const value = String(row[column.key] ?? "").trim();
      if (!value) return;
      payload[column.key] =
        column.type === "number" ? parseNumber(value) : value;
    });

    return payload;
  });

export const downloadBulkTemplate = async ({
  baseName,
  columns,
  sampleRow = {},
  format = "xlsx",
}) => {
  const XLSX = await loadXLSX();
  const headerRow = columns.map((column) => column.label);
  const sampleValues = columns.map((column) => sampleRow[column.key] ?? "");
  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, sampleValues]);

  columns.forEach((column, index) => {
    if (!column.text) return;
    const cellRef = XLSX.utils.encode_cell({ r: 1, c: index });
    if (worksheet[cellRef]) {
      worksheet[cellRef].z = "@";
      worksheet[cellRef].t = "s";
    }
  });

  worksheet["!cols"] = headerRow.map((label) => ({
    wch: Math.max(String(label).length + 4, 18),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

  if (format === "csv") {
    XLSX.writeFile(workbook, `${baseName}.csv`, { bookType: "csv" });
  } else {
    XLSX.writeFile(workbook, `${baseName}.xlsx`, { bookType: "xlsx" });
  }
};
